/**
 * `config.json` — the file that tells a client which environment it is talking to.
 *
 * **One shape, two clients.** `EdgeStack` writes this into the web bucket at deploy time;
 * the browser fetches it from its own origin and the app fetches it from the origin its
 * build names. Before this module the shape was declared once in `apps/web` and would
 * have been declared again in `apps/mobile` — two hand-written copies of a wire contract,
 * which drift in the direction of whichever client was edited last. The **resolution**
 * stays per-client, because the platforms genuinely differ: a browser defaults the API to
 * its own origin and builds a redirect from `window.location`, and a phone has neither.
 *
 * **Deliberately not TypeBox, and deliberately not in the barrel.** This is read by
 * clients, and `@going-once/contracts` pulls TypeBox and the whole event catalog in
 * behind it — the same reason `channels.ts` is imported by its own path. The validation
 * here is two field checks, which is what the file actually needs: it is written by our
 * own deploy, not submitted by a caller.
 */

/** The file as written. Every field optional: what is absent is a legitimate state. */
export interface RuntimeConfigFile {
  /** The public app client id the web signs in with. */
  webClientId?: string;
  /** The public app client id the phone signs in with. Absent in environments deployed before it existed. */
  nativeClientId?: string;
  /**
   * The app client staff sign in through: a Google ID token and nothing else, and the only
   * client whose tokens carry a staff role (GO-568). Absent before 2026-10-12.
   */
  staffClientId?: string;
  /**
   * The Google OAuth client the web's "Sign in with Google" asks for an ID token. Absent
   * where none is configured, and the button is not shown.
   */
  googleClientId?: string;
  /**
   * The pool both clients sign in to directly, with SRP (D52 addendum, 2026-09-27; the web
   * since 2026-10-07). Public: it is in the `iss` of every token the pool mints. Absent
   * before 2026-09-27.
   *
   * There is no `loginDomain` any more: managed login is gone (D35 addendum, 2026-10-07),
   * and an edge deployed before then still writes one, which nothing reads.
   */
  userPoolId?: string;
  /** Where capability calls go. The client's own origin when absent. */
  apiUrl?: string;
  /** Where listing photos are served from. `${origin}/media` when absent. */
  mediaUrl?: string;
  realtimeUrl?: string;
  realtimeHttpUrl?: string;
  realtimeApiKey?: string;
  stripePublishableKey?: string;
  /**
   * The shortest timed auction this environment accepts, in minutes. Absent from an edge
   * deployed before 2026-10-05; a client then assumes the product's fifteen. The server
   * enforces it either way, so this only decides which durations a screen offers.
   */
  auctionMinDurationMinutes?: number;
  /**
   * Which environment this is: `prod`, `integration`, or a personal sandbox. Not a secret and
   * not a setting, a fact. Used to keep team tools out of production (the web's CSV import
   * harness link, PR #44 review); absent on an edge deployed before 2026-10-03.
   */
  namespace?: string;
}

/**
 * The placeholder `ApiStack` creates the parameter as, before a person sets a real key.
 *
 * It is treated as absent rather than passed to Stripe: an environment where nobody has
 * set it yet is legitimate, and a screen saying "not configured here" is far better than
 * Stripe failing on submit inside an iframe at the worst possible moment.
 */
export const STRIPE_KEY_UNSET = 'pk_unset';

/** `pk_unset` and an empty string both mean "nobody has set one here". */
export function usableStripeKey(value: string | undefined): string | undefined {
  return value && value !== STRIPE_KEY_UNSET ? value : undefined;
}

/**
 * The two fields without which a client cannot sign anybody in: the pool, and the app
 * client this platform signs in with.
 *
 * Thrown rather than defaulted: every other value has a sensible fallback, and these two
 * do not. A missing one surfaces as a Cognito refusal that names nothing, which is a long
 * way from the file that is actually wrong.
 *
 * **The remedy is the caller's, and that is the whole point of the parameter.** The web
 * reads a local file `npm run web:config` writes, and the phone reads the deployed one, so
 * advice right for one is a dead end for the other. Only the caller knows which.
 */
export function requireSignInFields(
  file: RuntimeConfigFile,
  clientIdField: 'webClientId' | 'nativeClientId',
  remedy: string,
): { userPoolId: string; clientId: string } {
  const clientId = file[clientIdField];
  if (!file.userPoolId || !clientId) {
    throw new Error(`config.json is missing ${!file.userPoolId ? 'userPoolId' : clientIdField}. ${remedy}`);
  }
  return { userPoolId: file.userPoolId, clientId };
}
